import { MdEmail, MdPhone, MdLocalPhone, MdMenu, MdKeyboardArrowDown } from "react-icons/md";
import { FaFacebook, FaInstagram, FaYoutube, FaWhatsapp, FaDownload } from "react-icons/fa";
import { useState, useRef, useEffect, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { APIENDPOINT } from "../services/apiHelper";
import { schoolContext } from "../contexts/SchoolContextProvider";
import { mediaSource } from "../utilities/mediautilities";
import { useTranslation } from "react-i18next";
import { I18N_KEYS } from "../languages/PlaceHolder.js";

const Header = () => {
    const { schoolDetails } = useContext(schoolContext);
    const [selectedLanguage, setSelectedLanguage] = useState(localStorage.getItem('i18n') || 'en')
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);
    const [openSubmenu, setOpenSubmenu] = useState(null);
    const menuRef = useRef(null);

    const { i18n, t } = useTranslation();

    const navigate = useNavigate();

    // Check if any submenu item is active
    const isSubmenuActive = (item) => {
        if (item.submenu) {
            return item.submenu.some(subItem => location.pathname === subItem.path);
        }
        return false;
    };

    // Menu array with submenu support
    const menuItems = [
        { name: t(I18N_KEYS.HOMEPAGENAME), path: "/" },
        { name: t(I18N_KEYS.ABOUTPAGENAME), path: "/about" },
        { name: t(I18N_KEYS.DIGNATARIESPAGENAME), path: "/dignitaries" },
        {
            name: t(I18N_KEYS.HONORS),
            path: "#",
            submenu: [
                { name: t(I18N_KEYS.TOPSTUDENTPAGENAME), path: "/topstudents" },
                { name: t(I18N_KEYS.TOPTEACHERPAGENAME), path: "/topteachers" }
            ]
        },
        { name: t(I18N_KEYS.GALERYPAGENAME), path: "/gallery" },
        { name: t(I18N_KEYS.CONTACTUSPAGENAME), path: "/contact" }
    ];

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Auto-open submenu on mobile if current path matches a submenu item
    useEffect(() => {
        menuItems.forEach((item, index) => {
            if (item.submenu) {
                const isActive = item.submenu.some(subItem => location.pathname === subItem.path);
                if (isActive) {
                    setOpenSubmenu(index);
                }
            }
        });
    }, [location.pathname]);

    const linkClass = ({ isActive }) =>
        isActive
            ? "text-secondary font-medium transition-all"
            : "text-primary hover:text-secondary font-medium transition-all";


    const downloadBrochure = async () => {
        try {
            const fileUrl = APIENDPOINT + schoolDetails?.brochure?.fileUrl;

            const response = await fetch(fileUrl);
            const blob = await response.blob();

            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = `${schoolDetails?.name}.pdf`;
            document.body.appendChild(a);
            a.click();

            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download failed", error);
        }
    };

    const languages = [
        { title: "English", value: "en" },
        { title: "हिन्दी", value: "hi" },
        { title: "ગુજરાતી", value: "gu" }
    ]

    const setLanguage = (e) => {
        localStorage.setItem('i18n', e?.target?.value);
        i18n?.changeLanguage(e?.target?.value);
        setSelectedLanguage(e?.target?.value);
    }

    return (
        <>
            {/* Top Sub Header */}
            <div className="bg-primary text-xs sm:text-sm text-accent px-2 sm:px-4 py-1.5 sm:py-2">
                <div className="common-container flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-0">
                    {(schoolDetails?.email || schoolDetails?.contact_number || schoolDetails?.telephone_number) &&
                        <div className="flex items-center flex-wrap justify-center sm:justify-start gap-2 sm:gap-6">
                            {schoolDetails?.email &&
                                <span className="flex items-center gap-1.5 sm:gap-2">
                                    <MdEmail className="text-base sm:text-lg flex-shrink-0" />
                                    <span className="sm:inline">{schoolDetails?.email}</span>
                                </span>
                            }
                            {schoolDetails?.contact_number &&
                                <a
                                    href={`tel:+${schoolDetails?.contact_number}`}
                                    className="flex items-center gap-1.5 sm:gap-2 hover:text-secondary transition-colors"
                                >
                                    <MdPhone className="text-base sm:text-lg flex-shrink-0" />
                                    <span className="md:inline">{schoolDetails?.contact_number}</span>
                                </a>
                            }
                            {schoolDetails?.telephone_number &&
                                <a
                                    href={`tel:+${schoolDetails?.telephone_number}`}
                                    className="flex items-center gap-1.5 sm:gap-2 hover:text-secondary transition-colors"
                                >
                                    <MdLocalPhone className="text-base sm:text-lg flex-shrink-0" />
                                    <span className="md:inline">{schoolDetails?.telephone_number}</span>
                                </a>
                            }
                        </div>
                    }
                    <div className="flex items-center gap-2 sm:gap-4">
                        {schoolDetails?.facebook &&
                            <a
                                href={`${schoolDetails?.facebook}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-secondary transition-colors"
                                aria-label="Facebook"
                            >
                                <FaFacebook className="text-base sm:text-lg" />
                            </a>
                        }
                        {schoolDetails?.instagram &&
                            <a
                                href={`${schoolDetails?.instagram}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-secondary transition-colors"
                                aria-label="Instagram"
                            >
                                <FaInstagram className="text-base sm:text-lg" />
                            </a>
                        }
                        {schoolDetails?.youtube &&
                            <a
                                href={`${schoolDetails?.youtube}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-secondary transition-colors"
                                aria-label="YouTube"
                            >
                                <FaYoutube className="text-base sm:text-lg" />
                            </a>
                        }
                        {schoolDetails?.whatsapp_number &&
                            <a
                                href={`https://wa.me/${schoolDetails?.whatsapp_number}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-secondary transition-colors"
                                aria-label="WhatsApp"
                            >
                                <FaWhatsapp className="text-base sm:text-lg" />
                            </a>
                        }
                    </div>
                </div>
            </div>

            {/* Main Header */}
            <header className="bg-accent shadow-md sticky top-0 z-30 relative">
                <div className="common-container flex items-center justify-between py-4 px-4 relative">
                    <div className="flex items-center gap-4">
                        <img
                            src={mediaSource(schoolDetails?.logo?.fileUrl)}
                            alt="School Logo"
                            className="h-12 w-12 md:h-14 md:w-14 bg-white cursor-pointer"
                            onClick={() => navigate("/")}
                            onError={(e) => mediaSource(e)}
                        />
                        {(schoolDetails?.name || schoolDetails?.slogan) &&
                            <div className="flex flex-col leading-tight">
                                {schoolDetails?.name &&
                                    <span className=" md:text-xl font-bold text-primary">
                                        {schoolDetails?.name}
                                    </span>
                                }
                                {schoolDetails?.slogan &&
                                    <span className="text-[10px] md:text-xs text-primary font-medium">
                                        {schoolDetails?.slogan}
                                    </span>
                                }
                            </div>
                        }
                    </div>


                    {/* Desktop Menu */}
                    <div className="hidden md:flex items-center gap-6">
                        <nav className="flex gap-6 text-md font-medium text-white relative">
                            {menuItems.map((item, index) => (
                                <div
                                    key={index}
                                    className="relative"
                                >
                                    {item.submenu ? (
                                        <div
                                            className="relative group"
                                            onMouseEnter={() => setOpenSubmenu(index)}
                                            onMouseLeave={() => setOpenSubmenu(null)}
                                        >
                                            <button className={`${linkClass({ isActive: isSubmenuActive(item) })} flex items-center gap-1.5 cursor-pointer`}>
                                                {item.name}
                                                <MdKeyboardArrowDown className={`text-lg transition-transform duration-200 ${openSubmenu === index ? 'rotate-180' : ''}`} />
                                            </button>
                                            {openSubmenu === index && (
                                                <div
                                                    className="absolute top-full left-0 mt-0 w-56 bg-white rounded-lg shadow-2xl border border-gray-200 py-2 z-50"
                                                >
                                                    {item.submenu.map((subItem, subIndex) => (
                                                        <NavLink
                                                            key={subIndex}
                                                            to={subItem.path}
                                                            onClick={() => setOpenSubmenu(null)}
                                                            className={({ isActive }) =>
                                                                `block px-5 py-3 text-primary hover:bg-primary hover:text-accent transition-all ${isActive ? 'bg-primary/10 font-semibold text-secondary' : ''
                                                                }`
                                                            }
                                                        >
                                                            {subItem.name}
                                                        </NavLink>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <NavLink to={item.path} className={linkClass}>
                                            {item.name}
                                        </NavLink>
                                    )}
                                </div>
                            ))}
                        </nav>
                        {schoolDetails?.brochure &&
                            <button
                                className="px-4 py-2 border-2 border-primary text-primary font-semibold rounded hover:bg-primary hover:text-accent transition-colors"
                                onClick={() => { downloadBrochure() }}
                            >
                                {t(I18N_KEYS.DOWNLOAD)}
                            </button>
                        }
                        <select
                            onChange={(e) => { setLanguage(e) }}
                            value={selectedLanguage}
                            className="p-2 pl-3 pr-6 rounded border border-primary font-medium text-sm cursor-pointer transition"
                        >
                            {languages?.map((lang, idx) => (
                                <option key={idx} value={lang?.value}>{lang?.title}</option>
                            ))}
                        </select>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        className="md:hidden text-3xl text-primary hover:text-secondary transition-colors p-2"
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-label="Toggle menu"
                    >
                        <MdMenu />
                    </button>

                    {/* <button onClick={() => senddata()}>Send Data</button> */}

                    {/* Mobile Menu Overlay */}
                    <AnimatePresence>
                        {menuOpen && (
                            <>
                                {/* Backdrop */}
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    className="fixed inset-0 bg-black/50 z-40 md:hidden"
                                    onClick={() => setMenuOpen(false)}
                                />

                                {/* Mobile Menu Sidebar */}
                                <motion.div
                                    ref={menuRef}
                                    initial={{ x: '100%' }}
                                    animate={{ x: 0 }}
                                    exit={{ x: '100%' }}
                                    transition={{ type: 'tween', duration: 0.3 }}
                                    className="fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-50 md:hidden overflow-y-auto"
                                >
                                    {/* Menu Header */}
                                    <div className="bg-primary text-accent px-6 py-4 flex items-center justify-between border-b border-primary/20">
                                        <h3 className="text-lg font-bold">Menu</h3>
                                        <button
                                            onClick={() => setMenuOpen(false)}
                                            className="text-2xl hover:text-secondary transition-colors"
                                            aria-label="Close menu"
                                        >
                                            &times;
                                        </button>
                                    </div>

                                    {/* Menu Items */}
                                    <nav className="flex flex-col py-2">
                                        {menuItems.map((item, index) => (
                                            <div key={index}>
                                                {item.submenu ? (
                                                    <>
                                                        <button
                                                            onClick={() => setOpenSubmenu(openSubmenu === index ? null : index)}
                                                            className={`w-full px-6 py-4 text-left transition-all flex items-center justify-between ${isSubmenuActive(item)
                                                                ? 'bg-primary/10 text-primary font-semibold'
                                                                : 'text-gray-800 hover:bg-primary/10 hover:text-primary'
                                                                } ${openSubmenu === index ? 'bg-primary/10 text-primary' : ''
                                                                }`}
                                                        >
                                                            <span className="font-semibold text-base">{item.name}</span>
                                                            <MdKeyboardArrowDown
                                                                className={`text-xl transition-transform duration-200 ${openSubmenu === index ? 'rotate-180' : ''
                                                                    }`}
                                                            />
                                                        </button>
                                                        {openSubmenu === index && (
                                                            <div className="bg-gray-50/50">
                                                                {item.submenu.map((subItem, subIndex) => (
                                                                    <NavLink
                                                                        key={subIndex}
                                                                        to={subItem.path}
                                                                        onClick={() => {
                                                                            setMenuOpen(false);
                                                                            setOpenSubmenu(null);
                                                                        }}
                                                                        className={({ isActive }) =>
                                                                            `block px-8 py-3 text-gray-700 hover:bg-primary/10 hover:text-primary transition-all ${isActive ? 'bg-primary/15 text-primary font-semibold' : ''
                                                                            }`
                                                                        }
                                                                    >
                                                                        {subItem.name}
                                                                    </NavLink>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <NavLink
                                                        to={item.path}
                                                        onClick={() => setMenuOpen(false)}
                                                        className={({ isActive }) =>
                                                            `block px-6 py-4 text-gray-800 hover:bg-primary/10 hover:text-primary transition-all font-medium ${isActive ? 'bg-primary/10 text-primary font-semibold' : ''
                                                            }`
                                                        }
                                                    >
                                                        {item.name}
                                                    </NavLink>
                                                )}
                                            </div>
                                        ))}

                                        {/* Download Brochure */}
                                        {schoolDetails?.brochure && (
                                            <div className="border-t-2 border-gray-200 mt-2 pt-2">
                                                <button
                                                    onClick={() => {
                                                        setMenuOpen(false);
                                                        downloadBrochure();
                                                    }}
                                                    className="w-full px-6 py-4 text-left text-primary hover:bg-primary/10 transition-all font-semibold flex items-center gap-2"
                                                >
                                                    <span><FaDownload /></span>
                                                    <span>{t(I18N_KEYS.DOWNLOAD)}</span>
                                                </button>
                                            </div>
                                        )}
                                        <select
                                            onChange={(e) => setLanguage(e)}
                                            value={selectedLanguage}
                                            className="w-50 mt-2 p-3 rounded border border-primary font-medium text-sm cursor-pointer transition"
                                        >
                                            {languages?.map((lang, idx) => (
                                                <option key={idx} value={lang?.value}>
                                                    {lang?.title}
                                                </option>
                                            ))}
                                        </select>
                                    </nav>
                                </motion.div>
                            </>
                        )}
                    </AnimatePresence>
                </div>
            </header>
        </>
    );
}

export default Header;